import { reactive, toRefs } from 'vue'

const state = reactive({
  notifications: [],
  unreadCount: 0,
  totalCount: 0,
  lastFetchedAt: null
})

let pollInterval = null
let activePollers = 0
let currentAbortController = null

export function useNotifications () {
  const fetchRecent = async () => {
    if (currentAbortController) currentAbortController.abort()
    currentAbortController = new AbortController()

    try {
      const response = await fetch('/api/notifications?limit=20', {
        headers: { Accept: 'application/json' },
        signal: currentAbortController.signal
      })
      if (response.ok) {
        state.notifications = await response.json()

        const unreadRes = await fetch('/api/notifications/unread_count', {
          headers: { Accept: 'application/json' },
          signal: currentAbortController.signal
        })
        if (unreadRes.ok) {
          const unreadData = await unreadRes.json()
          state.unreadCount = unreadData.count || 0
          state.totalCount = unreadData.total || 0
        }

        state.lastFetchedAt = Date.now()
      }
    } catch (e) {
      if (e.name !== 'AbortError') {
        console.error('Failed to fetch notifications', e)
      }
    }
  }

  const markAllRead = async () => {
    // Optimistic UI update
    const previousUnread = state.unreadCount
    const previousState = JSON.stringify(state.notifications)

    state.unreadCount = 0
    state.notifications.forEach(n => {
      n.read = true
    })

    try {
      const token = document.querySelector('meta[name="csrf-token"]')?.content
      const response = await fetch('/api/notifications/mark_all_read', {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'X-CSRF-Token': token
        }
      })
      if (!response.ok) {
        throw new Error('Failed to mark as read')
      }
    } catch (e) {
      console.error(e)
      // Revert optimism
      state.unreadCount = previousUnread
      state.notifications = JSON.parse(previousState)
      window.showFlash?.('error', 'Failed to mark notifications as read')
      throw e
    }
  }

  const clearAll = async () => {
    const previousNotifications = [...state.notifications]
    const previousUnread = state.unreadCount
    const previousTotal = state.totalCount

    state.notifications = []
    state.unreadCount = 0
    state.totalCount = 0

    try {
      const token = document.querySelector('meta[name="csrf-token"]')?.content
      const response = await fetch('/api/notifications/clear', {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'X-CSRF-Token': token
        }
      })
      if (!response.ok) {
        throw new Error('Failed to clear notifications')
      }
    } catch (e) {
      console.error(e)
      state.notifications = previousNotifications
      state.unreadCount = previousUnread
      state.totalCount = previousTotal
      window.showFlash?.('error', 'Failed to clear notifications')
      throw e
    }
  }

  const markRead = async (notification) => {
    if (!notification || notification.read) return

    notification.read = true
    if (state.unreadCount > 0) {
      state.unreadCount = Math.max(0, state.unreadCount - 1)
    }

    try {
      const token = document.querySelector('meta[name="csrf-token"]')?.content
      fetch('/api/notifications/mark_all_read', {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-CSRF-Token': token
        },
        keepalive: true
      })
    } catch (e) {
      console.error('Failed to mark notification as read', e)
    }
  }

  const fetchUnreadCount = async () => {
    try {
      const unreadRes = await fetch('/api/notifications/unread_count', {
        headers: { Accept: 'application/json' }
      })
      if (unreadRes.ok) {
        const unreadData = await unreadRes.json()
        state.unreadCount = unreadData.count || 0
        state.totalCount = unreadData.total || 0
      }
    } catch (e) {
      // silent catch for background polling
    }
  }

  const startPolling = (intervalMs = 30000) => {
    activePollers++
    if (pollInterval) return // Already polling
    fetchUnreadCount()
    pollInterval = setInterval(fetchUnreadCount, intervalMs)
  }

  const stopPolling = () => {
    activePollers = Math.max(0, activePollers - 1)
    if (activePollers === 0 && pollInterval) {
      clearInterval(pollInterval)
      pollInterval = null
    }
  }

  return {
    ...toRefs(state),
    fetchRecent,
    fetchUnreadCount,
    markAllRead,
    markRead,
    clearAll,
    startPolling,
    stopPolling
  }
}
